import { z } from "zod";

/**
 * Shared DTO cho Controlled Ledger Correction (US-AE11.F5-0041 · @UC9104 controlled-ledger-correction).
 * MỘT nguồn sự thật cho api (S01) + admin web (S03). @gate FINANCE money/ledger 🔴.
 *
 * Correction = WORKFLOW 2-người-duyệt (4-eyes) để SỬA 1 bút toán sai. TUYỆT ĐỐI KHÔNG UPDATE/DELETE
 * sổ cái (G2 append-only) — "sửa" = append 1 REVERSAL qua domain (đảo cân bằng Σd=Σc, giữ bút toán gốc).
 *
 * BẤT BIẾN (invariants):
 * - G2: correction KHÔNG BAO GIỜ ghi/sửa sổ cái trực tiếp. DTO KHÔNG có field ghi tiền — chỉ workflow.
 * - 4-eyes: người duyệt ≠ người tạo (enforce ở DB CHECK + service). approve → post REVERSAL 1 lần.
 * - Idempotent: idempotency-key qua HEADER (scope actor,key,endpoint) — KHÔNG trong body.
 * - Money (nếu preview) = minor-unit string (BigInt an toàn qua JSON) — no-float.
 * - `.strict()` cho body ghi (create/approve) — chống over-post/field lạ.
 */

// ============================================================
// Enum-lite (khớp badge STATUS admin)
// ============================================================

/**
 * Trạng thái vòng đời correction:
 *  - pending_approval: đã tạo, chờ người thứ 2 duyệt.
 *  - applied: đã duyệt (bởi người ≠ creator) → REVERSAL đã append (reversalTxId set).
 *  - rejected: người duyệt từ chối → KHÔNG post reversal.
 */
export const correctionStatusSchema = z.enum(["pending_approval", "applied", "rejected"]);
export type CorrectionStatus = z.infer<typeof correctionStatusSchema>;

// ============================================================
// Correction item (list) + detail
// ============================================================

/** 1 dòng correction trên board list. Id/ref = con trỏ (KHÔNG PII/tiền thô). */
export const correctionItemSchema = z
  .object({
    id: z.string(),
    targetTxId: z.string(),
    status: correctionStatusSchema,
    reason: z.string(),
    sourceIncidentRef: z.string(),
    caseRef: z.string().nullable(),
    createdByAdminId: z.string(),
    approvedByAdminId: z.string().nullable(),
    reversalTxId: z.string().nullable(),
    createdAt: z.string(),
    appliedAt: z.string().nullable(),
  })
  .strict();

export type CorrectionItem = z.infer<typeof correctionItemSchema>;

/** Chi tiết correction = item (đủ field). detail trả nguyên item — không có sub-collection. */
export const correctionDetailSchema = correctionItemSchema;
export type CorrectionDetail = z.infer<typeof correctionDetailSchema>;

/** Envelope list: items + cursor pageInfo `{ data, pageInfo }`. */
export const correctionListResponseSchema = z
  .object({
    data: z.array(correctionItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type CorrectionListResponse = z.infer<typeof correctionListResponseSchema>;

// ============================================================
// Query (GET /admin/corrections)
// ============================================================

export const CORRECTION_LIST_MAX_LIMIT = 100;

/** Query list — filter status, cursor + limit. `.strict()` chặn param lạ. */
export const correctionListQuerySchema = z
  .object({
    status: correctionStatusSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(CORRECTION_LIST_MAX_LIMIT).optional(),
  })
  .strict();

export type CorrectionListQuery = z.infer<typeof correctionListQuerySchema>;

// ============================================================
// Create (POST /admin/corrections)
// ============================================================

export const CORRECTION_REASON_MIN_LENGTH = 8;
export const CORRECTION_REASON_MAX_LENGTH = 2000;
export const CORRECTION_INCIDENT_REF_MAX_LENGTH = 200;
export const CORRECTION_CASE_REF_MAX_LENGTH = 200;

/**
 * Body tạo correction. targetTxId (bút toán sai) uuid; reason bắt buộc (min len — bằng chứng);
 * sourceIncidentRef BẮT BUỘC (link sự cố — truy vết); caseRef optional (soft-link case 0038).
 * idempotency-key qua HEADER (KHÔNG trong body — scope actor,key,endpoint). status server-set. `.strict()`.
 */
export const createCorrectionSchema = z
  .object({
    targetTxId: z.string().uuid(),
    reason: z.string().min(CORRECTION_REASON_MIN_LENGTH).max(CORRECTION_REASON_MAX_LENGTH),
    sourceIncidentRef: z
      .string()
      .min(1)
      .max(CORRECTION_INCIDENT_REF_MAX_LENGTH),
    caseRef: z.string().max(CORRECTION_CASE_REF_MAX_LENGTH).optional(),
  })
  .strict();

export type CreateCorrectionBody = z.infer<typeof createCorrectionSchema>;

// ============================================================
// Approve / reject (POST /admin/corrections/:id/approve)
// ============================================================

export const CORRECTION_NOTE_MAX_LENGTH = 2000;

/**
 * Body duyệt correction. decision approve → post REVERSAL (người duyệt ≠ creator — 4-eyes); reject →
 * huỷ (KHÔNG reversal). note optional (ghi chú kết luận — audit). `.strict()`.
 */
export const approveCorrectionSchema = z
  .object({
    decision: z.enum(["approve", "reject"]),
    note: z.string().max(CORRECTION_NOTE_MAX_LENGTH).optional(),
  })
  .strict();

export type ApproveCorrectionBody = z.infer<typeof approveCorrectionSchema>;

// ============================================================
// Ledger-tx lookup theo account (GET /admin/ledger/accounts/:accountId/transactions)
// ============================================================
//
// T15 (@UC9157) DEAD-FLOW fix: correction bắt admin nhập `targetTxId` (UUID bút toán) nhưng KHÔNG có
// màn nào tra bút toán theo account → admin phải query DB tay (correction đứt ở khâu điền form). Endpoint
// READ-ONLY này liệt kê các bút toán (ledger transaction) chạm 1 account để admin CHỌN đúng bút toán sai
// → prefill targetTxId. INVARIANTS:
//  - READ-ONLY tuyệt đối (G2): CHỈ đọc ledger_lines/ledger_transactions. KHÔNG field mutating — DTO chỉ mô tả.
//  - B1 money: amountMinor = STRING minor units (BigInt an toàn wire) | null (redact vai thấp SUPPORT/READ_ONLY).
//  - `.strict()` chống field lạ.

export const LEDGER_TX_LOOKUP_MAX_LIMIT = 100;

/** Money minor-unit string (số nguyên, no-float). null khi redact vai không xem tiền. */
const ledgerMoneyMinorNullable = z
  .string()
  .regex(/^-?\d+$/, "money phải là số nguyên minor units (không float)")
  .nullable();

/** Chiều bút toán trên account (leg debit/credit). */
export const ledgerTxSideSchema = z.enum(["DEBIT", "CREDIT"]);
export type LedgerTxSide = z.infer<typeof ledgerTxSideSchema>;

/**
 * 1 bút toán (leg) chạm account — đủ field để admin nhận diện bút toán sai + chọn targetTxId:
 *  - txId: id giao dịch (LedgerTransaction) → prefill làm `targetTxId` cho correction.
 *  - lineId: id dòng ledger (keyset cursor — 1 tx có thể ≥1 leg trên cùng account).
 *  - txType: CONTRIBUTION | EXPENSE | REVERSAL | … (nhận diện loại).
 *  - side/amountMinor/currency: chiều + số tiền leg (amountMinor redact vai thấp → null).
 *  - memo: ghi chú (nullable). reversesTxId: non-null nếu leg thuộc bút toán REVERSAL (đã đảo — không sửa lại).
 *  - createdAt: ISO — hiển thị formatDateTime.
 */
export const ledgerTxLookupItemSchema = z
  .object({
    txId: z.string(),
    lineId: z.string(),
    txType: z.string(),
    side: ledgerTxSideSchema,
    amountMinor: ledgerMoneyMinorNullable,
    currency: z.string(),
    memo: z.string().nullable(),
    reversesTxId: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();

export type LedgerTxLookupItem = z.infer<typeof ledgerTxLookupItemSchema>;

/** Envelope list bút toán theo account `{ data, pageInfo }` (cursor keyset). */
export const ledgerTxLookupResponseSchema = z
  .object({
    data: z.array(ledgerTxLookupItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type LedgerTxLookupResponse = z.infer<typeof ledgerTxLookupResponseSchema>;

/** Query GET /admin/ledger/accounts/:accountId/transactions — cursor + limit. `.strict()` chặn param lạ. */
export const ledgerTxLookupQuerySchema = z
  .object({
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(LEDGER_TX_LOOKUP_MAX_LIMIT).optional(),
  })
  .strict();

export type LedgerTxLookupQuery = z.infer<typeof ledgerTxLookupQuerySchema>;
